<template>
	<fieldset class="mb-6">
		<legend class="h6" :class="{ 'is-invalid text-danger': typeError }">
			Which membership are you interested in?&ast;
		</legend>
		<div v-if="typeError" class="invalid-feedback mt-1 mb-2" data-testid="membership-type-error">
			{{ typeError }}
		</div>

		<div
			v-for="membershipType in props.membershipTypes"
			:key="membershipType.waitListItemId"
			class="card mb-3"
		>
			<div class="card-body d-flex">
				<div class="form-check mx-1">
					<input
						:id="membershipType.waitListItemId"
						v-model="typeValue"
						class="form-check-input"
						name="type"
						:value="membershipType.crmvalue"
						type="radio"
						:data-testid="`membership-type-${membershipType.waitListItemId}`"
					/>
				</div>
				<div class="flex-fill">
					<EnhanceFieldCollapse
						:membership-type="membershipType"
						:starting-price="props.startingPrice"
					/>
				</div>
			</div>
		</div>
	</fieldset>

	<fieldset>
		<legend class="h6">How Many Members?</legend>
		<div class="row mb-6">
			<div class="col-md-6 mb-6 mb-md-0">
				<div class="form-floating">
					<select
						id="adults"
						v-model="adultsValue"
						class="form-select"
						:class="{ 'is-invalid': adultsError }"
						aria-label="Adults 14 and older"
						data-testid="adults"
					>
						<option :value="null" disabled>Select number of adults</option>
						<option v-for="(value, index) in adults" :key="index" :value="value">
							{{ value }}
						</option>
					</select>
					<label for="adults">Adults 14 and older&ast;</label>
					<div class="invalid-feedback mt-1" data-testid="adults-error">
						{{ adultsError }}
					</div>
				</div>
			</div>
			<div class="col-md-6">
				<div class="form-floating">
					<select
						id="juniors"
						v-model="juniorsValue"
						class="form-select"
						:class="{ 'is-invalid': juniorsError }"
						aria-label="Juniors 13 and under"
						data-testid="juniors"
					>
						<option :value="null" disabled>Select number of juniors</option>
						<option v-for="(value, index) in juniors" :key="index" :value="value">
							{{ value }}
						</option>
					</select>
					<label for="juniors">Juniors 13 and under&ast;</label>
					<div class="invalid-feedback mt-1" data-testid="juniors-error">
						{{ juniorsError }}
					</div>
				</div>
			</div>
		</div>
	</fieldset>

	<div class="row mb-6">
		<div class="col">
			<label for="adults" class="h6">What are you most interested in?&ast;</label>
			<select
				id="interests"
				v-model="interestsValue"
				class="form-select"
				:class="{ 'is-invalid': interestsError }"
				aria-label="What are you most interested in?"
				data-testid="interests"
			>
				<option disabled :value="null">Select one interest</option>
				<option v-for="interest in props.interests" :key="interest.value" :value="interest.value">
					{{ interest.label }}
				</option>
			</select>
			<div class="invalid-feedback mt-1" data-testid="interests-error">
				{{ interestsError }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import EnhanceFieldCollapse from 'Lib/components/lead-form/EnhancedFieldCollapse.vue';
import type {
	AemuiWaitlistInterest,
	AemuiWaitlistItem
} from 'Types/aemui/content-fragments/contentFragment';

const props = withDefaults(
	defineProps<{
		membershipTypes: AemuiWaitlistItem[];
		startingPrice: string | null;
		interests: AemuiWaitlistInterest[];
	}>(),
	{
		membershipTypes: () => [] as AemuiWaitlistItem[],
		startingPrice: null,
		interests: () => [] as AemuiWaitlistInterest[]
	}
);

const adults = [1, 2, 3, 4];
const { errorMessage: adultsError, value: adultsValue } = useField<number | null>(
	'numberOfAdults',
	(value: number | null) => (value ? true : 'Please select a value'),
	{ initialValue: null }
);

const juniors = [0, 1, 2, 3, 4, 5, 6];
const { errorMessage: juniorsError, value: juniorsValue } = useField<number | null>(
	'numberOfJuniors',
	(value: number | null) => (typeof value === 'number' ? true : 'Please select a value'),
	{ initialValue: null }
);

const { errorMessage: typeError, value: typeValue } = useField<string>(
	'membershipType',
	(value) => (value ? true : 'Please select a membership'),
	{ initialValue: undefined }
);

const { errorMessage: interestsError, value: interestsValue } = useField<string | null>(
	'interests',
	(value) => (value ? true : 'Please select an interest'),
	{ initialValue: null }
);
</script>

<style lang="scss" scoped>
.card {
	background-color: var(--kpl-contrast-200);
	border: 0;

	&-body {
		padding-bottom: 1.5rem;
		padding-top: 1.5rem;
	}

	:deep(.membership-description p:last-child) {
		margin-bottom: 0;
	}
}
</style>
